import AddRounded from '@mui/icons-material/AddRounded';
import BrokenImageRounded from '@mui/icons-material/BrokenImageRounded';
import LocalShippingRounded from '@mui/icons-material/LocalShippingRounded';
import MoreVertRounded from '@mui/icons-material/MoreVertRounded';
import StarRounded from '@mui/icons-material/StarRounded';
import WarehouseRounded from '@mui/icons-material/WarehouseRounded';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { api, ApiError, unwrap } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Money } from '../../components/Money';
import { EmptyState } from '../../components/states';
import { useNotify } from '../../lib/notify';
import { InventoryLayout } from './InventoryLayout';
import { idem, inventoryKeys, LOCATION_KIND_LABELS, useLocations, type Location } from './model';

const KIND_ICON = {
  STORE: <WarehouseRounded />,
  DAMAGED: <BrokenImageRounded />,
  TRANSIT: <LocalShippingRounded />,
} as const;

const KIND_TONE = { STORE: 'primary', DAMAGED: 'error', TRANSIT: 'info' } as const;

const locationSchema = z.object({
  name: z.string().trim().min(1, 'Enter a name').max(120, 'At most 120 characters'),
  kind: z.enum(['STORE', 'DAMAGED', 'TRANSIT']),
  isSellable: z.boolean(),
});
type LocationValues = z.infer<typeof locationSchema>;

function LocationDialog({
  open,
  location,
  onClose,
}: {
  open: boolean;
  location: Location | null;
  onClose: () => void;
}) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const { control, handleSubmit, formState, setError, setValue } = useForm<LocationValues>({
    resolver: zodResolver(locationSchema),
    values: location
      ? { name: location.name, kind: location.kind, isSellable: location.isSellable }
      : { name: '', kind: 'STORE', isSellable: true },
  });
  const kind = useWatch({ control, name: 'kind' });

  const submit = handleSubmit(async (v) => {
    try {
      if (location) {
        await unwrap(
          api.PATCH('/locations/{id}', {
            params: { path: { id: location.id } },
            body: { name: v.name, isSellable: v.isSellable, version: location.version },
          }),
        );
        notify.success('Location updated');
      } else {
        await unwrap(api.POST('/locations', { params: { header: idem() }, body: v }));
        notify.success(`${v.name} added`);
      }
      await queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
      onClose();
    } catch (e) {
      if (e instanceof ApiError && e.details.some((d) => d.path.endsWith('name')))
        setError('name', { message: e.details.find((d) => d.path.endsWith('name'))?.message });
      else notify.error(e);
    }
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <Box component="form" noValidate onSubmit={(e) => void submit(e)}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {location ? 'Edit location' : 'Add a location'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.25} sx={{ pt: 1 }}>
            <Controller
              control={control}
              name="name"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Name"
                  autoFocus
                  placeholder="e.g. Warehouse B"
                  error={Boolean(formState.errors.name)}
                  helperText={formState.errors.name?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="kind"
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Kind"
                  disabled={Boolean(location)}
                  helperText={location ? 'The kind is fixed once created.' : undefined}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    setValue('isSellable', e.target.value === 'STORE');
                  }}
                >
                  {(Object.keys(LOCATION_KIND_LABELS) as Location['kind'][]).map((k) => (
                    <MenuItem key={k} value={k}>
                      {LOCATION_KIND_LABELS[k]}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <Controller
              control={control}
              name="isSellable"
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Sell from here
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {kind === 'DAMAGED'
                          ? 'Damaged stock is usually not sellable.'
                          : 'Stock here counts as available for sales.'}
                      </Typography>
                    </Box>
                  }
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" loading={formState.isSubmitting}>
            {location ? 'Save' : 'Add location'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

export function LocationsPage() {
  const { can } = useAuth();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const { data: locations, isLoading } = useLocations();
  const manage = can('settings.manage');
  const valuation = useQuery({
    queryKey: inventoryKeys.valuation,
    queryFn: () => unwrap(api.GET('/stock/valuation')),
    enabled: can('products.view_cost'),
  });
  const [dialog, setDialog] = useState<{ open: boolean; location: Location | null }>({
    open: false,
    location: null,
  });
  const [menu, setMenu] = useState<{ anchor: HTMLElement; location: Location } | null>(null);
  const [deactivate, setDeactivate] = useState<Location | null>(null);

  const patch = useMutation({
    mutationFn: (args: { location: Location; body: { isDefault?: true; isActive?: boolean } }) =>
      unwrap(
        api.PATCH('/locations/{id}', {
          params: { path: { id: args.location.id } },
          body: { ...args.body, version: args.location.version },
        }),
      ),
    onSuccess: async (loc, args) => {
      await queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
      notify.success(
        args.body.isDefault
          ? `${loc.name} is now the default location`
          : loc.isActive
            ? `${loc.name} reactivated`
            : `${loc.name} deactivated`,
      );
    },
    onError: (e) => notify.error(e),
  });

  const sorted = [...(locations ?? [])].sort(
    (a, b) =>
      Number(b.isActive) - Number(a.isActive) ||
      Number(b.isDefault) - Number(a.isDefault) ||
      a.name.localeCompare(b.name),
  );

  return (
    <InventoryLayout
      subtitle="Where you keep stock. The default location is used when a document doesn’t name one."
      actions={
        manage && (
          <Button
            variant="contained"
            startIcon={<AddRounded />}
            onClick={() => setDialog({ open: true, location: null })}
          >
            Add location
          </Button>
        )
      }
    >
      {isLoading ? (
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'repeat(3, 1fr)' },
          }}
        >
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rounded" height={150} />
          ))}
        </Box>
      ) : sorted.length === 0 ? (
        <Card>
          <EmptyState
            icon={<WarehouseRounded />}
            title="No locations"
            body="Add the shop floor, a warehouse or a damaged-goods shelf."
            action={
              manage
                ? {
                    label: 'Add location',
                    onClick: () => setDialog({ open: true, location: null }),
                  }
                : undefined
            }
          />
        </Card>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: {
              xs: 'minmax(0,1fr)',
              sm: 'repeat(2, 1fr)',
              lg: 'repeat(3, 1fr)',
            },
          }}
        >
          {sorted.map((l) => {
            const value = valuation.data?.locations.find((v) => v.locationId === l.id)?.value;
            return (
              <Card
                key={l.id}
                sx={{
                  opacity: l.isActive ? 1 : 0.62,
                  transition: 'transform 160ms ease, box-shadow 160ms ease',
                  '&:hover': { transform: 'translateY(-2px)' },
                }}
              >
                <CardContent>
                  <Stack direction="row" spacing={1.75} sx={{ alignItems: 'flex-start' }}>
                    <Box
                      sx={(t) => {
                        const c = (t.vars ?? t).palette[KIND_TONE[l.kind]].main;
                        return {
                          width: 46,
                          height: 46,
                          flexShrink: 0,
                          borderRadius: '13px',
                          display: 'grid',
                          placeItems: 'center',
                          color: c,
                          background: `linear-gradient(135deg, color-mix(in srgb, ${c} 18%, transparent), color-mix(in srgb, ${c} 5%, transparent))`,
                          border: `1px solid color-mix(in srgb, ${c} 20%, transparent)`,
                        };
                      }}
                    >
                      {KIND_ICON[l.kind]}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                        <Typography variant="h6" noWrap>
                          {l.name}
                        </Typography>
                        {l.isDefault && (
                          <StarRounded
                            sx={{ fontSize: 18, color: 'warning.main' }}
                            aria-label="Default"
                          />
                        )}
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {LOCATION_KIND_LABELS[l.kind]}
                      </Typography>
                    </Box>
                    {manage && (
                      <IconButton
                        aria-label={`Actions for ${l.name}`}
                        onClick={(e) => setMenu({ anchor: e.currentTarget, location: l })}
                        size="small"
                      >
                        <MoreVertRounded />
                      </IconButton>
                    )}
                  </Stack>
                  <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', rowGap: 1 }}>
                    {l.isDefault && <Chip size="small" color="primary" label="Default" />}
                    <Chip
                      size="small"
                      variant="outlined"
                      label={l.isSellable ? 'Sellable' : 'Not sellable'}
                    />
                    {!l.isActive && <Chip size="small" variant="outlined" label="Inactive" />}
                  </Stack>
                  {value !== undefined && (
                    <Stack
                      direction="row"
                      sx={{
                        mt: 2,
                        pt: 1.5,
                        borderTop: 1,
                        borderColor: 'divider',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Stock value
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        <Money value={value} />
                      </Typography>
                    </Stack>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      <Menu anchorEl={menu?.anchor} open={Boolean(menu)} onClose={() => setMenu(null)}>
        <MenuItem
          onClick={() => {
            setDialog({ open: true, location: menu?.location ?? null });
            setMenu(null);
          }}
        >
          Edit
        </MenuItem>
        {menu && !menu.location.isDefault && menu.location.isActive && (
          <MenuItem
            onClick={() => {
              patch.mutate({ location: menu.location, body: { isDefault: true } });
              setMenu(null);
            }}
          >
            Make default
          </MenuItem>
        )}
        {menu && menu.location.isActive && !menu.location.isDefault && (
          <MenuItem
            sx={{ color: 'error.main' }}
            onClick={() => {
              setDeactivate(menu.location);
              setMenu(null);
            }}
          >
            Deactivate
          </MenuItem>
        )}
        {menu && !menu.location.isActive && (
          <MenuItem
            onClick={() => {
              patch.mutate({ location: menu.location, body: { isActive: true } });
              setMenu(null);
            }}
          >
            Reactivate
          </MenuItem>
        )}
      </Menu>

      <LocationDialog
        open={dialog.open}
        location={dialog.location}
        onClose={() => setDialog({ open: false, location: null })}
      />
      <ConfirmDialog
        open={Boolean(deactivate)}
        title={`Deactivate ${deactivate?.name ?? ''}?`}
        body="It will no longer be offered on new documents. A location that still holds stock can’t be deactivated — move or adjust the stock first."
        confirmLabel="Deactivate"
        danger
        onConfirm={() =>
          deactivate
            ? patch
                .mutateAsync({ location: deactivate, body: { isActive: false } })
                .catch(() => undefined)
            : Promise.resolve()
        }
        onClose={() => setDeactivate(null)}
      />
    </InventoryLayout>
  );
}
